'use client';

import { Banknote, CreditCard, QrCode } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { formatTotemPrice } from '@/app/(users)/totem-by-slug/[slug]/utils/totem-price';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import type { PaymentMethod } from '@/interface/payment-method';
import { cn } from '@/lib/utils';

type Props = {
  methods: PaymentMethod[];
  onSelect: (method: PaymentMethod) => void;
  cartTotal: number;
  amountTendered: string;
  onAmountTenderedChange: (amount: string) => void;
  isPending?: boolean;
};

export function TotemPaymentMethodPicker({
  methods,
  onSelect,
  cartTotal,
  amountTendered,
  onAmountTenderedChange,
  isPending,
}: Props) {
  const [pendingMethodId, setPendingMethodId] = useState<string | null>(null);

  if (methods.length === 0) {
    return <p className="text-sm text-muted-foreground">No hay métodos de pago configurados para este tótem.</p>;
  }

  const parsedAmount = parseFloat(amountTendered) || 0;
  const change = Math.max(0, parsedAmount - cartTotal);

  const cashMethod = methods.find(m => m.type === 'efectivo');
  const qrMethod = methods.find(m => m.type === 'qr');
  const mpLinkMethod = methods.find(m => m.type === 'mercado_pago');
  const cardMethod = methods.find(m => m.type === 'clover' || m.type === 'getnet');

  const paymentOptions = [
    cashMethod && {
      method: cashMethod,
      label: 'Efectivo',
      icon: <Banknote className="size-6 text-emerald-600" />,
      bg: 'bg-emerald-100',
    },
    qrMethod && {
      method: qrMethod,
      label: 'QR',
      icon: <QrCode className="size-6 text-orange-500" />,
      bg: 'bg-orange-100',
    },
    mpLinkMethod && {
      method: mpLinkMethod,
      label: 'Pagar con MercadoPago',
      icon: <Image src="/mercado-pago.svg" alt="Mercado Pago" width={32} height={32} className="object-contain" />,
      bg: 'bg-blue-100',
    },
    cardMethod && {
      method: cardMethod,
      label: 'Tarjeta',
      icon: <CreditCard className="size-6 text-cyan-600" />,
      bg: 'bg-cyan-100',
    },
  ].filter((o): o is NonNullable<typeof o> => Boolean(o));

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xs font-bold tracking-wider text-muted-foreground uppercase">Método de pago</h3>
      <div className="flex flex-col gap-3">
        {paymentOptions.map(option => {
          const isThisPending = isPending && pendingMethodId === option.method.id;
          return (
            <button
              key={option.method.id}
              type="button"
              disabled={isPending}
              className={cn(
                'relative flex items-center gap-4 rounded-xl border p-4 text-left transition-colors border-border/60',
                !isPending && 'hover:border-border hover:bg-muted/30 active:scale-[0.98]',
                isPending && !isThisPending && 'opacity-50 cursor-not-allowed',
                isThisPending && 'opacity-100 ring-2 ring-primary border-primary cursor-not-allowed'
              )}
              onClick={() => {
                setPendingMethodId(option.method.id);
                onSelect(option.method);
              }}
            >
              <div className={cn('flex size-12 items-center justify-center rounded-lg', option.bg)}>{option.icon}</div>
              <span className="text-lg font-bold flex-1">{option.label}</span>
              {isThisPending && <Spinner className="size-5 shrink-0 bg-primary" />}
            </button>
          );
        })}
      </div>

      {cashMethod && (
        <div className="mt-4 flex flex-col md:flex-row gap-6 items-end border-t pt-6">
          <div className="flex-1 space-y-2">
            <label htmlFor="amount-tendered" className="text-xs font-medium text-muted-foreground">
              Calculadora de Vuelto (Opcional)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-lg text-foreground">$</span>
              <Input
                id="amount-tendered"
                type="number"
                value={amountTendered}
                onChange={e => onAmountTenderedChange(e.target.value)}
                className="pl-8 h-12 text-xl font-bold shadow-none"
                placeholder="0"
              />
            </div>
          </div>
          <div className="flex-1 space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Vuelto</span>
            <div className="h-12 flex items-center">
              <span className="text-xl font-bold text-emerald-600">{formatTotemPrice(change)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
